import {
  cloneElement,
  type CSSProperties,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMemoizedFn, useUpdateEffect } from 'ahooks';
import { Form, message } from 'antd';
import { isArray, isString, isUndefined } from '@dimjs/lang';
import { classNames, get } from '@dimjs/utils';
import {
  getUuid,
  isUndefinedOrNull,
  type TAny,
  type TPlainObject,
} from '@flatbiz/utils';
import { fbaHooks } from '../fba-hooks/index.js';
import { PaginationWrapper } from '../pagination-wrapper/pagination.jsx';
import { columnFoldCache, filterCache, firstQueryCache } from './cache.js';
import { EasyTableContext } from './context.js';
import { FoldOperateDropdown } from './fold-operate-dropdown.jsx';
import {
  type EasyTableProps,
  type EasyTableRefApi,
  type TEasyTableTableColumn,
} from './type.js';
import { getFormFieldsValue, requestParamsInternalAdapter } from './utils.js';
import './style.less';

export const EasyTable = forwardRef<EasyTableRefApi, EasyTableProps>(
  (props, ref) => {
    const screenType = fbaHooks.useResponsivePoint() || '';
    const [dataSource, setDataSource] = useState<TPlainObject[]>();
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const columnFoldConfig = props.columnFoldConfig;
    const asyncColumnRequest = props.asyncColumnRequest;

    // 记录是否发起过请求
    const alreadyRequestFlag = useRef<boolean>(false);
    const removeRequestParamsEmptyValue =
      props.removeRequestParamsEmptyValue === undefined
        ? true
        : props.removeRequestParamsEmptyValue;

    const [columnFoldOpen, setColumnFoldOpen] = useState(false);

    const cacheKey = useMemo(() => {
      if (props.cacheKey) {
        try {
          const iframe = window.frameElement;
          const iframeId = iframe?.id || '';
          return `${props.cacheKey}_${iframeId}`;
        } catch (_error) {
          return props.cacheKey;
        }
      }
      return `${Date.now()}-${getUuid()}`;
    }, [props.cacheKey]);

    const cacheParams = filterCache.get(cacheKey);

    const [showFoldKeyList, setShowFoldKeyList] = useState<string[]>(() => {
      if (cacheKey) {
        const columnFoldCacheValue = columnFoldCache.get(cacheKey);
        const cacheKeyList = isArray(columnFoldCacheValue)
          ? columnFoldCacheValue.filter((item) => isString(item))
          : [];
        return cacheKeyList.length > 0
          ? cacheKeyList
          : columnFoldConfig?.initSelectedKeys || [];
      } else {
        return columnFoldConfig?.initSelectedKeys || [];
      }
    });

    const pageSize = props.pageSize || 10;

    const cancelInitAutoRequest = useMemo(() => {
      if (typeof props.cancelInitAutoRequest === 'function') {
        return props.cancelInitAutoRequest();
      }
      if (!isUndefinedOrNull(props.cancelInitAutoRequest)) {
        return props.cancelInitAutoRequest;
      }
      return props.initRequest == false ? true : false;
    }, [props]);

    const alwaysCancelInitAutoRequest = useMemo(() => {
      if (typeof props.alwaysCancelInitAutoRequest === 'function') {
        return props.alwaysCancelInitAutoRequest();
      }
      return props.alwaysCancelInitAutoRequest;
    }, [props]);

    const paginationStatusRef = useRef(false);
    const respOriginalDataRef = useRef<TAny>();
    const baseColumnsRef = useRef<TEasyTableTableColumn<TPlainObject>[]>([]);
    const [dynamicColumns, setDynamicColumns] =
      useState<TEasyTableTableColumn<TPlainObject>[]>();

    const columnsRef = useRef<TEasyTableTableColumn<TPlainObject>[]>([]);

    const fieldNames = {
      list: 'list',
      total: 'total',
      pageNo: 'pageNo',
      pageSize: 'pageSize',
      ...props.fieldNames,
    };
    const outerForm = Form.useFormInstance();
    const [form] = Form.useForm(props.form || outerForm);

    const onInnerRequest = useMemoizedFn(async (params) => {
      try {
        if (props.breforeRequest) {
          try {
            await props.breforeRequest(form);
          } catch (_error) {
            return;
          }
        }
        firstQueryCache.set(cacheKey, false);
        const paramsAll = filterCache.set(cacheKey, params);
        setLoading(true);
        const {
          requestParamsAdapter,
          onRequest,
          requestResultAdapter,
          dynamicColumsAdapter,
        } = props.serviceConfig;

        let paramsNew = requestParamsInternalAdapter(paramsAll, {
          removeEmptyValue: removeRequestParamsEmptyValue,
        });

        paramsNew = requestParamsAdapter
          ? requestParamsAdapter(paramsNew, paramsAll)
          : paramsNew;
        const respData = (await onRequest(paramsNew)) || {};
        alreadyRequestFlag.current = true;
        respOriginalDataRef.current = respData;
        props.onDataSourceChange?.(respData);
        let respDataNew: TPlainObject = respData;
        if (requestResultAdapter) {
          respDataNew = requestResultAdapter(respData);
        }
        if (dynamicColumsAdapter) {
          const dynamicColumns = dynamicColumsAdapter(
            respData,
            baseColumnsRef.current
          );
          /** 不要添加默认值  */
          setDynamicColumns(dynamicColumns);
        }
        const respList = get(respDataNew, fieldNames.list);
        setDataSource(isArray(respList) ? respList : []);
        setTotal(get(respDataNew, fieldNames.total));
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        if (props.onRequestErrorHandle) {
          props.onRequestErrorHandle(error);
        } else {
          void message.error(error?.message || '未知异常');
        }
      }
    });

    fbaHooks.useEffectCustom(() => {
      const hasPagination = getPaginationStatus();
      const firstQueryFlag = firstQueryCache.get(cacheKey);
      /** 缓存的优先级高于 initialValues */
      form.setFieldsValue({
        ...props.initialValues,
        ...cacheParams,
      });
      if (alwaysCancelInitAutoRequest) {
        filterCache.set(cacheKey, props.initialValues || {});
        return;
      }
      if (!cancelInitAutoRequest || !firstQueryFlag) {
        if (hasPagination) {
          void onInnerRequest({
            [fieldNames.pageNo]: 1,
            [fieldNames.pageSize]: pageSize,
            ...props.initialValues,
            ...cacheParams,
          });
        } else {
          void onInnerRequest(props.initialValues);
        }
      } else {
        filterCache.set(cacheKey, props.initialValues || {});
      }
    }, []);

    const clearQueryCondition = useMemoizedFn((values?: TPlainObject) => {
      filterCache.remove(cacheKey);
      form.resetFields();
      if (values) {
        filterCache.set(cacheKey, values);
        form.setFieldsValue(values);
      }
    });

    const updateQueryCondition = useMemoizedFn((values: TPlainObject) => {
      filterCache.set(cacheKey, values);
      form.setFieldsValue(values);
    });

    const getPaginationData = (pageNo: number, _pageSize: number) => {
      if (getPaginationStatus()) {
        return {
          [fieldNames.pageNo]: pageNo,
          [fieldNames.pageSize]: _pageSize || pageSize,
        };
      } else {
        return {
          [fieldNames.pageNo]: undefined,
          [fieldNames.pageSize]: undefined,
        };
      }
    };

    const onResetRequest = useMemoizedFn((values?: TPlainObject) => {
      const params = {
        ...getPaginationData(1, cacheParams[fieldNames.pageSize]),
        ...props.initialValues,
        ...values,
      };
      filterCache.remove(cacheKey);
      form.resetFields();
      if (values) {
        form.setFieldsValue(values);
      }
      void onInnerRequest(params);
    });

    const getRequestParams = useMemoizedFn(() => {
      const cacheParams = filterCache.get(cacheKey);
      const parameAll = {
        ...cacheParams,
        ...form.getFieldsValue(),
      };
      let params: TPlainObject = {
        ...getPaginationData(1, parameAll[fieldNames.pageSize]),
      };
      if (props.filterFormItemKeys?.length) {
        props.filterFormItemKeys.forEach((item) => {
          params[item] = parameAll[item];
        });
      } else {
        params = { ...parameAll, ...params };
      }

      return requestParamsInternalAdapter(params, {
        removeEmptyValue: removeRequestParamsEmptyValue,
      });
    });

    const onOpenColumnFoldModal = useMemoizedFn(() => {
      setColumnFoldOpen(true);
    });

    const onCloseColumnFoldModal = useMemoizedFn(() => {
      setColumnFoldOpen(false);
    });

    const getSubmitParams = useMemoizedFn(() => {
      const cacheParams = filterCache.get(cacheKey);
      let params: TPlainObject = {
        ...getPaginationData(1, cacheParams[fieldNames.pageSize]),
      };
      if (props.filterFormItemKeys?.length) {
        props.filterFormItemKeys.forEach((item) => {
          params[item] = cacheParams[item];
        });
      } else {
        params = { ...cacheParams, ...params };
      }

      return requestParamsInternalAdapter(params, {
        removeEmptyValue: removeRequestParamsEmptyValue,
      });
    });

    const getEasyTableRef = (): EasyTableRefApi => {
      return {
        /**
         * 参数
         * 1. 不用添加已存在的搜索条件
         * 2. 此处可重新
         */
        onRequest: onInnerRequest,
        clearQueryCondition,
        updateQueryCondition,
        getRequestParams,
        getSubmitParams,
        onResetRequest,
        form,
        dataSource: respOriginalDataRef.current,
        getDataSource: () => {
          return respOriginalDataRef.current;
        },
        onFilterDataSource: (filterDataSource) => {
          setDataSource(filterDataSource);
        },
        getTableDataList: () => {
          return dataSource || [];
        },
        onClearDataSource: () => {
          setTotal(0);
          setDataSource([]);
        },
        onUpdateDataSource: (dataList) => {
          setDataSource(dataList);
          respOriginalDataRef.current = { [fieldNames.list]: dataList };
          props.onDataSourceChange?.(respOriginalDataRef.current);
        },
        columnFoldTriggerRender: (
          children,
          columns: TEasyTableTableColumn<TPlainObject>[]
        ) => {
          if (props.columnFoldConfig?.triggerType === 'drawer') {
            return cloneElement(children, {
              onClick: onOpenColumnFoldModal,
            });
          }

          const foldColumnList = columns.filter((item: TAny) => {
            return !item.hidden && item.isFold && item.dataIndex;
          });

          return (
            <FoldOperateDropdown
              dataList={
                foldColumnList as { dataIndex: string; title: string }[]
              }
              onChange={onChangeFoldColumnList}
              cacheKey={cacheKey}
              initSelectedRowKeys={showFoldKeyList}
            >
              {children}
            </FoldOperateDropdown>
          );
        },
        onChangeTableColumns: (columns) => {
          setDynamicColumns(columns);
        },
        getTableColumns: () => {
          return columnsRef.current;
        },
        loading,
        setEnabledInitRequest: () => {
          firstQueryCache.set(cacheKey, false);
        },
      };
    };

    useImperativeHandle(ref, () => {
      return getEasyTableRef();
    });

    const onSetPaginationStatus = (status: boolean) => {
      paginationStatusRef.current = status;
    };

    const onSetBaseColumns = (base) => {
      baseColumnsRef.current = base;
    };

    const onSetColumns = (columns) => {
      columnsRef.current = columns;
    };
    const onFormFinish = () => {
      if (loading) {
        console.log('loading...');
        return;
      }
      const values = getFormFieldsValue(form, props.filterFormItemKeys);
      if (props.onFormFinish) {
        props.onFormFinish({
          ...values,
          ...getPaginationData(1, cacheParams[fieldNames.pageSize]),
        });
      } else {
        void onInnerRequest({
          ...values,
          ...getPaginationData(1, cacheParams[fieldNames.pageSize]),
        });
      }
    };
    const isFull = isUndefined(props.isFull) ? true : props.isFull;

    const filterFixedFt = screenType === 'xs' ? false : props.filterFixed;
    const className = classNames(
      'fba-easy-table',
      {
        'fba-easy-table-full': isFull,
        'fba-easy-table-filter-fixed': filterFixedFt,
        'fba-easy-table-pagination-fixed': props.paginationFixed,
      },
      props.className
    );

    const paginationData = fbaHooks.useMemoCustom(() => {
      return {
        showSizeChanger: true,
        current: cacheParams[fieldNames.pageNo] || 1,
        pageSize: cacheParams[fieldNames.pageSize] || pageSize,
        total: total,
        showTotal: (total) => `共 ${total} 条记录`,
        ...props.pagination,
      };
    }, [
      fieldNames.pageNo,
      cacheParams,
      fieldNames.pageSize,
      cacheKey,
      pageSize,
      props.pagination,
      total,
    ]);

    const onChangeFoldColumnList = (keyList: string[]) => {
      setShowFoldKeyList(keyList);
      columnFoldConfig?.onChange?.(keyList);
    };

    const onChange = useMemoizedFn((page: number, pageSize: number) => {
      const params = {
        [fieldNames.pageSize]: pageSize,
        [fieldNames.pageNo]: page,
      };
      filterCache.set(cacheKey, params);
      void onInnerRequest(params);
      props.pagination?.onChange?.(page, pageSize);
    });

    const getPaginationStatus = () => {
      return props.paginationFixed || paginationStatusRef.current;
    };

    // 分页参数发生变更
    useUpdateEffect(() => {
      if (props.paginationFixed) {
        void onInnerRequest({
          [fieldNames.pageSize]: cacheParams[fieldNames.pageSize] || pageSize,
          [fieldNames.pageNo]: 1,
        });
      } else {
        void onInnerRequest({
          [fieldNames.pageSize]: undefined,
          [fieldNames.pageNo]: undefined,
        });
      }
    }, [props.paginationFixed]);

    const wrapperClassName = classNames(
      'fba-easy-table-wrapper',
      {
        'fba-easy-table-wrapper-inline': props.isInline,
      },
      props.className
    );

    const wrapperStyle: CSSProperties = {
      overflowY: props.filterFixed || props.paginationFixed ? 'hidden' : 'auto',
      ...props.style,
    };

    const children =
      typeof props.children === 'function'
        ? props.children(respOriginalDataRef.current)
        : props.children;

    const showCancelInitRequestTips = alreadyRequestFlag.current
      ? false
      : cancelInitAutoRequest || alwaysCancelInitAutoRequest;

    return (
      <EasyTableContext.Provider
        value={{
          onSetBaseColumns,
          onSetColumns,
          getEasyTableRef,
          cacheKey,
          onRequest: onInnerRequest,
          tableDataSource: dataSource,
          tableTotal: total,
          loading,
          fieldNames,
          pageSize,
          onSetPaginationStatus,
          getPaginationStatus,
          onFormFinish,
          form,
          paginationFixed: props.paginationFixed || false,
          foldKeys: props.foldKeys || [],
          columns: dynamicColumns,
          initialValues: props.initialValues,
          filterFormItemKeys: props.filterFormItemKeys,
          showCancelInitRequestTips: showCancelInitRequestTips,
          dynamicColumnsConfig: {
            showFoldKeyList,
            onChangeShowFoldKeyList: onChangeFoldColumnList,
            columnFoldConfig,
            onOpenColumnFoldModal,
            onCloseColumnFoldModal,
            columnFoldOpen,
            asyncColumnRequest,
          },
        }}
      >
        {props.paginationFixed ? (
          <div className={wrapperClassName} style={wrapperStyle}>
            <div className={className}>{children}</div>
            {total > 0 && (
              <div className="fba-easy-table-pagination">
                <PaginationWrapper
                  size="small"
                  {...paginationData}
                  onChange={onChange}
                />
              </div>
            )}
          </div>
        ) : (
          <div className={wrapperClassName} style={props.style}>
            <div className={className}>{children}</div>
          </div>
        )}
      </EasyTableContext.Provider>
    );
  }
);
